import { useState, useEffect } from 'react'

interface CurrencyData {
  country: string
  countryCode: string
  currency: string
  symbol: string
  flag: string
  localPrice: string
  localPriceStrikethrough: string
  usdPrice: number
  checkoutUrl: string
  isLoading: boolean
  isUSD: boolean
}

// Rates ~5-8% above Hotmart's effective rate so the landing price
// is just barely above the checkout price after marketing rounding.
const CURRENCY_MAP: Record<
  string,
  {
    currency: string
    symbol: string
    rate: number
    flag: string
    decimals: boolean
  }
> = {
  AR: { currency: 'ARS', symbol: '$', rate: 1140, flag: '🇦🇷', decimals: false },
  CL: { currency: 'CLP', symbol: '$', rate: 1100, flag: '🇨🇱', decimals: false },
  MX: { currency: 'MXN', symbol: '$', rate: 21.5, flag: '🇲🇽', decimals: false },
  CO: { currency: 'COP', symbol: '$', rate: 4500, flag: '🇨🇴', decimals: false },
  PE: { currency: 'PEN', symbol: 'S/', rate: 4.0, flag: '🇵🇪', decimals: true },
  UY: { currency: 'UYU', symbol: '$', rate: 45, flag: '🇺🇾', decimals: false },
  PY: { currency: 'PYG', symbol: '₲', rate: 8200, flag: '🇵🇾', decimals: false },
  BO: { currency: 'BOB', symbol: 'Bs', rate: 7.5, flag: '🇧🇴', decimals: true },
  CR: { currency: 'CRC', symbol: '₡', rate: 550, flag: '🇨🇷', decimals: false },
  EC: { currency: 'USD', symbol: '$', rate: 1, flag: '🇪🇨', decimals: true },
  GT: { currency: 'GTQ', symbol: 'Q', rate: 8.3, flag: '🇬🇹', decimals: true },
  HN: { currency: 'HNL', symbol: 'L', rate: 27, flag: '🇭🇳', decimals: false },
  NI: { currency: 'NIO', symbol: 'C$', rate: 39, flag: '🇳🇮', decimals: false },
  PA: { currency: 'USD', symbol: '$', rate: 1, flag: '🇵🇦', decimals: true },
  DO: { currency: 'DOP', symbol: 'RD$', rate: 65, flag: '🇩🇴', decimals: false },
  VE: { currency: 'USD', symbol: '$', rate: 1, flag: '🇻🇪', decimals: true },
  ES: { currency: 'EUR', symbol: '€', rate: 0.99, flag: '🇪🇸', decimals: true },
  US: { currency: 'USD', symbol: '$', rate: 1, flag: '🇺🇸', decimals: true },
}

const COUNTRY_NAMES: Record<string, string> = {
  AR: 'Argentina',
  CL: 'Chile',
  MX: 'México',
  CO: 'Colombia',
  PE: 'Perú',
  UY: 'Uruguay',
  PY: 'Paraguay',
  BO: 'Bolivia',
  CR: 'Costa Rica',
  EC: 'Ecuador',
  GT: 'Guatemala',
  HN: 'Honduras',
  NI: 'Nicaragua',
  PA: 'Panamá',
  DO: 'República Dominicana',
  VE: 'Venezuela',
  ES: 'España',
  US: 'Estados Unidos',
}

const BASE_CHECKOUT_URL = 'https://pay.hotmart.com/V104120573X?checkoutMode=10'
const USD_PRICE = 13.99
const USD_STRIKETHROUGH = 47.0

function formatCharmPrice(rawPrice: number, useDecimals: boolean): number {
  if (useDecimals) {
    // Currencies with decimals: end in .90
    return Math.floor(rawPrice) + 0.9
  }

  // Integer currencies — round up to clean marketing numbers
  if (rawPrice >= 10000) {
    // e.g. 15,389 → ceil(15.389) * 1000 = 16,000 → 16,000 - 100 = 15,900
    const thousands = Math.ceil(rawPrice / 1000) * 1000
    return thousands - 100
  } else if (rawPrice >= 1000) {
    // e.g. 7,699 → ceil(76.99) * 100 = 7,700 → 7,700 - 10 = 7,690
    // But let's use: ceil to nearest 100, subtract 10 → ends in 90
    const hundreds = Math.ceil(rawPrice / 100) * 100
    return hundreds - 10
  } else if (rawPrice >= 100) {
    // e.g. 300.79 → ceil(30.079) * 10 = 310 → 310 - 1 = 309
    const tens = Math.ceil(rawPrice / 10) * 10
    return tens - 1
  } else {
    // e.g. 55.96 → ceil(5.596) * 10 = 60 → 60 - 1 = 59
    const tens = Math.ceil(rawPrice / 10) * 10
    return tens - 1
  }
}

function formatNumber(num: number, useDecimals: boolean): string {
  if (useDecimals) {
    return num.toLocaleString('es-ES', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  }
  return Math.round(num).toLocaleString('es-ES')
}

export function useLocalCurrency(): CurrencyData {
  const [data, setData] = useState<CurrencyData>({
    country: 'Estados Unidos',
    countryCode: 'US',
    currency: 'USD',
    symbol: '$',
    flag: '🇺🇸',
    localPrice: '13.90',
    localPriceStrikethrough: '46.90',
    usdPrice: USD_PRICE,
    checkoutUrl: BASE_CHECKOUT_URL,
    isLoading: true,
    isUSD: true,
  })

  useEffect(() => {
    const cached = sessionStorage.getItem('userCurrency_v6')

    if (cached) {
      try {
        const parsed = JSON.parse(cached)
        setData({ ...parsed, isLoading: false })
        return
      } catch {
        // Continue to fetch
      }
    }

    const fetchCountry = async () => {
      try {
        const response = await fetch('https://ipapi.co/json/')
        const json = await response.json()
        const countryCode = json.country_code || 'US'

        const currencyInfo = CURRENCY_MAP[countryCode] || CURRENCY_MAP.US
        const countryName = COUNTRY_NAMES[countryCode] || 'Estados Unidos'

        const localPriceRaw = USD_PRICE * currencyInfo.rate
        const strikePriceRaw = USD_STRIKETHROUGH * currencyInfo.rate

        const charmPrice = formatCharmPrice(
          localPriceRaw,
          currencyInfo.decimals,
        )
        const charmStrike = formatCharmPrice(
          strikePriceRaw,
          currencyInfo.decimals,
        )

        const localPrice = formatNumber(charmPrice, currencyInfo.decimals)
        const localPriceStrikethrough = formatNumber(
          charmStrike,
          currencyInfo.decimals,
        )

        const isUSD = currencyInfo.currency === 'USD'

        const newData: CurrencyData = {
          country: countryName,
          countryCode,
          currency: currencyInfo.currency,
          symbol: currencyInfo.symbol,
          flag: currencyInfo.flag,
          localPrice,
          localPriceStrikethrough,
          usdPrice: USD_PRICE,
          checkoutUrl: BASE_CHECKOUT_URL,
          isLoading: false,
          isUSD,
        }

        sessionStorage.setItem('userCurrency_v6', JSON.stringify(newData))
        setData(newData)
      } catch {
        setData((prev) => ({ ...prev, isLoading: false }))
      }
    }

    fetchCountry()
  }, [])

  return data
}
